/**
 * Provider Selector — Payme / Click selection with official colored logos.
 * White card backgrounds for logo visibility on dark theme.
 * Logos bundled as base64 via import (instant load, zero network delay).
 */

import React from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import paymeLogo from '../../../assets/payme-logo.png'
import clickLogo from '../../../assets/click-logo.png'

type Provider = 'payme' | 'click'

const PROVIDERS: { id: Provider; logo: string; label: string }[] = [
  { id: 'payme', logo: paymeLogo, label: 'Payme' },
  { id: 'click', logo: clickLogo, label: 'Click' },
]

interface ProviderSelectorProps {
  selected: Provider
  onChange: (provider: Provider) => void
}

export const ProviderSelector: React.FC<ProviderSelectorProps> = ({ selected, onChange }) => {
  const { t } = useTranslation()

  return (
    <div>
      <p className="text-xs font-medium text-text-hint mb-2">{t('premium.selectProvider')}</p>
      <div className="grid grid-cols-2 gap-3">
        {PROVIDERS.map(({ id, logo, label }) => {
          const isActive = selected === id
          return (
            <button
              key={id}
              onClick={() => { onChange(id); hapticImpact('light') }}
              className={`relative rounded-xl border-2 transition-colors duration-200 bg-[#f5f5f7] overflow-hidden ${
                isActive
                  ? 'border-primary/50'
                  : 'border-white/[0.08] hover:border-white/[0.15]'
              }`}
            >
              {/* Light card for logo — only this area is light */}
              <div className="rounded-lg bg-[#f5f5f7] flex items-center justify-center py-3.5 px-4">
                <img
                  src={logo}
                  alt={label}
                  className="h-6 w-auto object-contain"
                  draggable={false}
                />
              </div>

              {/* Selected checkmark */}
              {isActive && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
                  <Check size={12} className="text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
